import React, { lazy } from 'react';
import { Navigate, Route, useParams } from 'react-router-dom';
import { asyncRouteLoader } from 'utils/asyncRouteLoader';
import { SuspenseOutlet } from 'utils/routing/SuspenseOutlet';
import MessageAttachmentProvider from 'containers/AIAssistant/MessageAttachmentProvider';
import { IntegrationsRoute } from 'containers/AIAssistant/constants';
import { getUser } from 'auth';
import { useFlag } from 'utils/flags/hooks';
import { FEATURE_AI_AGENT_GUIDANCE } from 'utils/flags/constants';
import { hasPermission } from 'containers/AIAssistant/permissions';
import { useGetAIAssistantMembership } from 'containers/AIAssistant/_api/useGetAIAssistantMembership';
import RouteAnalytics from './RouteAnalytics';

const AIAssistantList = lazy(() => import('containers/AIAssistant/ListView'));
const AIAssistantDetails = lazy(
  () => import('containers/AIAssistant/Details/AIAssistantDetails')
);
const AIAssistantDashboard = lazy(
  () => import('containers/AIAssistant/Details/Dashboard/Dashboard')
);
const AIAssistantSources = lazy(
  () => import('containers/AIAssistant/Details/Sources/Sources')
);
const DashboardConversationChat = lazy(
  () =>
    import('containers/AIAssistant/Details/Dashboard/DashboardConversationChat')
);
const AIAssistantActions = lazy(
  () => import('containers/AIAssistant/Details/Actions/Actions')
);
const AIAssistantSharepointExchange = lazy(
  () =>
    import(
      'containers/AIAssistant/Details/Sources/SharepointSourceModal/SharepointExchangeRedirect'
    )
);
const AIAssistantGoogleDriveExchange = lazy(() =>
  import(
    'containers/AIAssistant/Details/Sources/GoogleDriveSourceModal/GoogleDriveExchangeRedirect'
  ).then((m) => ({ default: m.GoogleDriveExchangeRedirect }))
);

const AIAssistantGuidance = lazy(() =>
  import('containers/AIAssistant/Details/Guidance/Guidance').then((m) => ({
    default: m.Guidance,
  }))
);
const AIAssistantCustomization = lazy(
  () => import('containers/AIAssistant/Details/Customization/Customization')
);
const AIAssistantPermissions = asyncRouteLoader(
  () => import('containers/AIAssistant/Details/Permissions'),
  [
    {
      type: 'reducer',
      reducerKey: 'assistantPermissions.members',
      importPromise: () =>
        import('containers/AIAssistant/Details/Permissions/reducerUsers'),
    },
    {
      type: 'saga',
      importPromise: () =>
        import('containers/AIAssistant/Details/Permissions/sagas'),
    },
  ]
);
const AIAssistantSettings = lazy(
  () => import('containers/AIAssistant/Details/Settings/Settings')
);
const AIAssistantIntegrations = lazy(
  () => import('containers/AIAssistant/Details/Integrations')
);
const AIAssistantVerifyEmail = lazy(
  () => import('containers/AIAssistant/Details/VerifyEmail')
);
const AIAssistantLandingPage = lazy(
  () => import('containers/AIAssistant/LandingPage/LandingPage')
);

const CitationsDetailPage = lazy(() =>
  import('containers/AIAssistant/LandingPage/CitationsDetailPage').then(
    (m) => ({ default: m.CitationsDetailPage })
  )
);

const AiAssistantGuardLoader = asyncRouteLoader(async () => {
  const user = getUser();
  const isAiEnabled = user?.isAIAssistantEnabled;
  return {
    default: () =>
      isAiEnabled ? <SuspenseOutlet /> : <Navigate to="/app" replace />,
  };
});

const AIAssistantGuidanceGuard = () => {
  const { id } = useParams<{ id: string }>();
  const isGuidanceEnabled = useFlag(FEATURE_AI_AGENT_GUIDANCE);
  const { data: membership, isLoading } = useGetAIAssistantMembership({
    id: id ?? '',
  });
  const permissions = membership?.permission ?? [];

  if (!id || !isGuidanceEnabled) {
    return (
      <Navigate
        to={id ? `/app/ai-assistant/${id}/dashboard` : '/app/ai-assistant'}
        replace
      />
    );
  }

  if (isLoading) {
    return null;
  }

  if (!hasPermission('MANAGE_CUSTOMIZATION', permissions)) {
    return <Navigate to={`/app/ai-assistant/${id}/dashboard`} replace />;
  }

  return (
    <RouteAnalytics pageName="Agent Guidance">
      <AIAssistantGuidance assistantId={id} />
    </RouteAnalytics>
  );
};

export const aiAssistantRoutes = () => (
  <Route path="ai-assistant" element={<AiAssistantGuardLoader />}>
    <Route index element={<AIAssistantList />} />
    <Route
      path="sharepoint/exchange"
      element={<AIAssistantSharepointExchange />}
    />
    <Route
      path="googledrive/exchange"
      element={<AIAssistantGoogleDriveExchange />}
    />
    <Route
      path=":id/start"
      element={
        <RouteAnalytics pageName="AI Assistant Landing Page">
          <MessageAttachmentProvider>
            <AIAssistantLandingPage />
          </MessageAttachmentProvider>
        </RouteAnalytics>
      }
    />
    <Route
      path=":id/conversation/:conversationId/message/:messageId/citation/:citationId/start"
      element={
        <RouteAnalytics pageName="AI Assistant Citation Details Page">
          <CitationsDetailPage />
        </RouteAnalytics>
      }
    />
    <Route
      path=":id"
      element={
        <RouteAnalytics pageName="AI Assistant Details">
          <AIAssistantDetails />
        </RouteAnalytics>
      }
    >
      <Route index element={<Navigate to="dashboard" replace />} />
      <Route
        path="dashboard"
        element={
          <RouteAnalytics pageName="Dashboard">
            <AIAssistantDashboard />
          </RouteAnalytics>
        }
      >
        <Route path=":conversationId" element={<DashboardConversationChat />} />
      </Route>
      <Route
        path="sources"
        element={
          <RouteAnalytics pageName="Sources">
            <AIAssistantSources />
          </RouteAnalytics>
        }
      />
      <Route
        path="actions"
        element={
          <RouteAnalytics pageName="Actions">
            <AIAssistantActions />
          </RouteAnalytics>
        }
      />
      <Route path="guidance" element={<AIAssistantGuidanceGuard />} />
      <Route
        path="customization"
        element={
          <RouteAnalytics pageName="Customization">
            <AIAssistantCustomization />
          </RouteAnalytics>
        }
      />
      <Route
        path="permissions"
        element={
          <RouteAnalytics pageName="Permissions">
            <AIAssistantPermissions />
          </RouteAnalytics>
        }
      />
      <Route
        path="settings"
        element={
          <RouteAnalytics pageName="Settings">
            <AIAssistantSettings />
          </RouteAnalytics>
        }
      />
      <Route
        path={IntegrationsRoute}
        element={
          <RouteAnalytics pageName="Integrations">
            <AIAssistantIntegrations />
          </RouteAnalytics>
        }
      />
      <Route
        path="verify-email/:token"
        element={
          <RouteAnalytics pageName="Verify Email AI">
            <AIAssistantVerifyEmail />
          </RouteAnalytics>
        }
      />
    </Route>
  </Route>
);
